import {
  type DocumentsPendingState,
  type DocumentsStoreState,
  fetchDocuments,
  type Filters,
  type FilterStoreState,
  type PeriodFilters,
  toggleDocumentTypeInCategory } from '../../../../store/slices';
import {
  type DocumentDTO,
  type DocumentGroupType,
} from '../../../../types';
import { DocumentGroupName } from './DocumentGroupName';
import { Documents } from './Documents';
import {
  type FC,
  useEffect,
  useState,
} from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import { type AppDispatch } from 'store';

type DocumentGroupProps = DocumentGroupType & {
  isNewDocumentsCategory: boolean,
  topLevelCategoryId: string,
};

type PaginationState = {
  currentPage: number,
  hasMore: boolean,
  isInitialLoad: boolean,
};

// Constants for pagination
const DEFAULT_PAGE = 1;
const PAGE_SIZE = 10;

export const DocumentGroup: FC<DocumentGroupProps> = ({
  categoryId,
  documentGroupId,
  documentGroupName,
  documents,
  isNewDocumentsCategory,
  isOpen,
  numberOfDocuments,
  numberOfNewDocuments,
  topLevelCategoryId,
}) => {
  const paginationKey = `${categoryId}-${documentGroupId}-${isNewDocumentsCategory}`;
  const [paginationStates, setPaginationStates] = useState<Record<string, PaginationState>>({});
  const currentPaginationState = paginationStates[paginationKey] || {
    currentPage: DEFAULT_PAGE,
    hasMore: true,
    isInitialLoad: false,
  };

  const activeFilters = useSelector<{
    filters: FilterStoreState,
  }>((state) => state.filters.activeFilters) as Filters | null;

  const {
    displayType,
  } = useSelector<{ documents: DocumentsStoreState, }>(
    (state) => state.documents,
  ) as DocumentsStoreState;

  const periodFilter = useSelector<{
    filters: FilterStoreState,
  }>((state) => state.filters.usualDocumentsFilter) as PeriodFilters | null;

  const documentPendingState = useSelector<{ documents: DocumentsStoreState, }>(
    (state) => state
      .documents
      .documentsFetchQueue
      .find((pendingState) => pendingState.categoryId === categoryId &&
        pendingState.documentGroupId === documentGroupId &&
        pendingState.isNewDocumentsCategory === isNewDocumentsCategory),
  ) as DocumentsPendingState;
  const dispatch: AppDispatch = useDispatch();

  const setDefaultPagination = (key: string) => {
    setPaginationStates((prev) => ({
      ...prev,
      [key]: {
        currentPage: DEFAULT_PAGE,
        hasMore: true,
        isInitialLoad: true,
      },
    }));
  };

  const toggleGroupTypeOnClick = (
    event?: React.MouseEvent<HTMLElement>,
  ) => {
    event?.stopPropagation();
    dispatch(toggleDocumentTypeInCategory({
      documentGroupId,
      isNewDocumentsCategory,
      isOpen: !isOpen,
      topLevelCategoryId,
    }));
  };

  const toggleGroupTypeOnTab = (
    event?: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    event?.stopPropagation();

    if (event && event.key !== 'Tab') {
      return;
    }

    dispatch(toggleDocumentTypeInCategory({
      documentGroupId,
      isNewDocumentsCategory,
      isOpen: !isOpen,
      topLevelCategoryId,
    }));
  };

  const onDocumentsRetry = (
    event: React.MouseEvent<HTMLElement>,
  ) => {
    event.stopPropagation();
    if (activeFilters && periodFilter) {
      setDefaultPagination(paginationKey);
      dispatch(fetchDocuments({
        addToQueue: false,
        categoryId,
        fileType: documentGroupId,
        filter: {
          filterByNewDocuments: Boolean(isNewDocumentsCategory),
          firmId: activeFilters.organizationId,
          ...activeFilters,
          displayType: displayType.id,
        },
        periodFilter,
      }));
    }
  };

  const loadMoreDocuments = (event: React.MouseEvent<HTMLElement, MouseEvent> | undefined) => {
    event?.stopPropagation();
    if (activeFilters && periodFilter && currentPaginationState.hasMore && !documentPendingState) {
      const nextPage = currentPaginationState.currentPage + 1;
      dispatch(fetchDocuments({
        addToQueue: true,
        categoryId,
        fileType: documentGroupId,
        filter: {
          filterByNewDocuments: Boolean(isNewDocumentsCategory),
          firmId: activeFilters.organizationId,
          ...activeFilters,
          displayType: displayType.id,
        },
        page: nextPage,
        periodFilter,
        size: PAGE_SIZE,
      // eslint-disable-next-line promise/prefer-await-to-then
      })).unwrap().then((fetchedDocuments) => {
        if (fetchedDocuments.length > 0) {
          setPaginationStates((prev) => ({
            ...prev,
            [paginationKey]: {
              ...prev[paginationKey],
              currentPage: nextPage,
            },
          }));
        }

        if (fetchedDocuments.length < PAGE_SIZE) {
          setPaginationStates((prev) => ({
            ...prev,
            [paginationKey]: {
              ...prev[paginationKey],
              hasMore: false,
              isInitialLoad: false,
            },
          }));
        }
      });
    }
  };

  useEffect(() => {
    if (
      isOpen &&
      !documents &&
      !documentPendingState &&
      activeFilters && periodFilter
    ) {
      if (!paginationStates[paginationKey]) {
        setPaginationStates((prev) => ({
          ...prev,
          [paginationKey]: {
            currentPage: DEFAULT_PAGE,
            hasMore: true,
            isInitialLoad: true,
          },
        }));
      }

      dispatch(fetchDocuments({
        addToQueue: true,
        categoryId,
        fileType: documentGroupId,
        filter: {
          filterByNewDocuments: Boolean(isNewDocumentsCategory),
          firmId: activeFilters.organizationId,
          ...activeFilters,
          displayType: displayType.id,
        },
        page: DEFAULT_PAGE,
        periodFilter,
        size: PAGE_SIZE,
      // eslint-disable-next-line promise/prefer-await-to-then
      })).unwrap().then(() => {
        setPaginationStates((prev) => ({
          ...prev,
          [paginationKey]: {
            ...prev[paginationKey],
            hasMore: numberOfDocuments > PAGE_SIZE,
            isInitialLoad: false,
          },
        }));
      });
    } else if (!paginationStates[paginationKey] && documents) {
      setPaginationStates((prev) => ({
        ...prev,
        [paginationKey]: {
          currentPage: DEFAULT_PAGE,
          hasMore: numberOfDocuments > PAGE_SIZE && numberOfDocuments > documents.length,
          isInitialLoad: false,
        },
      }));
    }
  }, [
    activeFilters,
    categoryId,
    dispatch,
    documentGroupId,
    documentPendingState,
    documents,
    isNewDocumentsCategory,
    isOpen,
    periodFilter,
    displayType.id,
    paginationKey,
    paginationStates,
  ]);

  return (
    <div
      aria-expanded={isOpen}
      className='overflow-visible'
      data-test={`document-group-${documentGroupName}`}
      onClick={toggleGroupTypeOnClick}
      onKeyDown={toggleGroupTypeOnTab}
      role='button'
      tabIndex={0}
    >
      {
        isOpen ?
          <div>
            <DocumentGroupName
              categoryId={categoryId}
              documentGroupId={documentGroupId}
              documentGroupName={documentGroupName}
              documents={documents as DocumentDTO[]}
              isOpen={isOpen}
              numberOfDocuments={numberOfDocuments}
              numberOfNewDocuments={numberOfNewDocuments}
            />
            <Documents
              categoryId={categoryId}
              documentGroupName={documentGroupName}
              documents={documents}
              documentsPendingState={documentPendingState}
              loadMoreDocuments={loadMoreDocuments}
              onDocumentsRetry={onDocumentsRetry}
              showViewMoreButton={currentPaginationState.hasMore && !currentPaginationState.isInitialLoad}
            />
          </div> :
          <div className='flex flex-1 flex-col items-stretch rounded-main'>
            <DocumentGroupName
              categoryId={categoryId}
              documentGroupId={documentGroupId}
              documentGroupName={documentGroupName}
              documents={documents as DocumentDTO[]}
              isOpen={isOpen}
              numberOfDocuments={numberOfDocuments}
              numberOfNewDocuments={numberOfNewDocuments}
            />
          </div>
      }
    </div>
  );
};
